import { useEffect, useRef, useState } from 'react';

import create from 'zustand';

import { SizedRelativeSpinner } from '@components/loading-spinner';
import { Modal, hideModal, useModal } from '@components/modal';
import { useIsModalActive } from '@components/modal/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { getScopedTranslate } from '@locale/translate';
import { second } from '@utils/milliseconds';

import { useCurrentCompany, useInvalidateAuthentication } from 'api/authentication/hooks';
import { useWebSocket } from 'api/hooks';
import { useInvalidateWorkspaces } from 'api/workspaces/hooks';

const createMessageStore = () => {
  type State = {
    messages: string[];
    isCompleted: boolean;
    playbackDone: boolean;
    addMessage: (msg: string) => void;
    setCompleted: () => void;
    markPlaybackDone: () => void;
  };

  const useStore = create<State>((set, get) => {
    return {
      messages: [],
      isCompleted: false,
      playbackDone: false,

      addMessage: (message) => {
        set((state) => ({ messages: [...state.messages, message] }));
      },

      setCompleted: () => {
        set({ isCompleted: true });
      },

      markPlaybackDone: () => {
        set({ playbackDone: true });
      },
    };
  });

  return {
    useMessages: () => useStore((s) => s.messages),
    useIsCompleted: () => useStore((s) => s.isCompleted),
    usePlaybackDone: () => useStore((s) => s.playbackDone),
    addMessage: (msg: string) => useStore.getState().addMessage(msg),
    setCompleted: () => useStore.getState().setCompleted(),
    markPlaybackDone: () => useStore.getState().markPlaybackDone(),
  };
};

export const useInitializingModal = () => {
  const company = useCurrentCompany();
  const { invalidateCurrentUser } = useInvalidateAuthentication();
  const { invalidateWorkspaces } = useInvalidateWorkspaces();

  const connectionStateRef = useRef({
    shouldConnect: false,
    hasCompleted: false,
    hasShownModal: false,
  });

  const storeRef = useRef(createMessageStore());
  const { useMessages, usePlaybackDone, addMessage, setCompleted } = storeRef.current;
  const playbackDone = usePlaybackDone();
  const messages = useMessages();

  useEffect(() => {
    if (
      company?.shortId &&
      !company.hasFinishedInitializing &&
      !connectionStateRef.current.hasCompleted &&
      !connectionStateRef.current.shouldConnect
    ) {
      connectionStateRef.current.shouldConnect = true;
    }
  }, [company?.shortId, company?.hasFinishedInitializing]);

  const path = company?.shortId ? `companies/${company.shortId}/initialized/` : '';

  const handler = (e: MessageEvent) => {
    const data = JSON.parse(e.data as string);
    addMessage(data.message);

    if (data.isCompleted) {
      connectionStateRef.current.shouldConnect = false;
      connectionStateRef.current.hasCompleted = true;
      setCompleted();
    }
  };

  useWebSocket(path, handler, {
    enabled: connectionStateRef.current.shouldConnect && !connectionStateRef.current.hasCompleted,
    throttleTime: 0,
  });

  if (playbackDone && !connectionStateRef.current.hasCompleted) {
    connectionStateRef.current.hasCompleted = true;
    connectionStateRef.current.shouldConnect = false;
  }

  const showModal = useModal(InitializingModal, {
    variant: 'REGULAR',
    backgroundColor: 'backgroundPrimary',
    hasBlurredAndForcedBackdrop: true,
  });
  const isAnyModalActive = useIsModalActive();
  const hasPreview = localStorage.getItem('has-self-service-feature-preview') === 'true';

  useEffect(() => {
    if (!hasPreview) return;
    if (messages.length > 0 && !isAnyModalActive && !connectionStateRef.current.hasShownModal) {
      connectionStateRef.current.hasShownModal = true;
      showModal({ store: storeRef.current });
    }
  }, [messages.length, isAnyModalActive, hasPreview, showModal]);

  useEffect(() => {
    if (playbackDone) {
      invalidateCurrentUser();
      invalidateWorkspaces();
      hideModal();
    }
  }, [playbackDone, invalidateCurrentUser, invalidateWorkspaces]);
};

export const InitializingModal = ({ store }: { store: ReturnType<typeof createMessageStore> }) => {
  const { t } = getScopedTranslate(locale);
  const messages = store.useMessages();
  const isCompleted = store.useIsCompleted();
  const { markPlaybackDone } = store;

  const [visibleMessage, setVisibleMessage] = useState('');

  const queueRef = useRef<string[]>([]);
  const runningRef = useRef(false);
  const completedRef = useRef(false);

  useEffect(() => {
    if (messages.length > queueRef.current.length) {
      queueRef.current.push(...messages.slice(queueRef.current.length));
    }
  }, [messages]);

  useEffect(() => {
    completedRef.current = isCompleted;
  }, [isCompleted]);

  useEffect(() => {
    if (runningRef.current) return;
    if (queueRef.current.length === 0 && !completedRef.current) return;
    runningRef.current = true;
    (async () => {
      while (queueRef.current.length > 0 || !completedRef.current) {
        if (queueRef.current.length > 0) {
          const next = queueRef.current.shift()!;
          setVisibleMessage(next);
          await new Promise((r) => setTimeout(r, second));
        } else {
          await new Promise((r) => setTimeout(r, 100));
        }
      }
      markPlaybackDone();
      hideModal();
    })();
  }, [messages.length, markPlaybackDone]);

  return (
    <Modal>
      <Modal.Content>
        <Stack space={6}>
          <Stack space={2} align="center">
            <Text font="bold14" color="indigo80" text={t('title')} />
            <Text font="heading1" color="textPrimary" text={visibleMessage || t('lastDetails')} />
          </Stack>
          <SizedRelativeSpinner size={64} />
        </Stack>
      </Modal.Content>
    </Modal>
  );
};

const locale = {
  en: {
    title: 'Just a moment',
    lastDetails: 'Getting the last details together...',
  },
};
